import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import {
  Cpu,
  Wifi,
  WifiOff,
  ChevronDown,
  ChevronRight,
  Terminal,
  Rocket,
  RotateCcw,
  Clock,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Trash2,
  Loader,
  Eye,
  EyeOff,
  Copy,
  Check,
} from "lucide-react";

interface ThrottleFlags {
  raw?: string;
  underVoltageNow?: boolean;
  underVoltageEver?: boolean;
  armCappedNow?: boolean;
  armCappedEver?: boolean;
  throttledNow?: boolean;
  throttledEver?: boolean;
  softTempNow?: boolean;
  softTempEver?: boolean;
}

interface DeviceHealth {
  tempC?: number;
  cpuFreqMhz?: number;
  cpuFreqMaxMhz?: number;
  load1?: number;
  load5?: number;
  load15?: number;
  coreVolts?: number;
  powerWatts?: number;
  fanRpm?: number;
  coolingState?: number;
  throttle?: ThrottleFlags;
  memTotalMb?: number;
  memUsedMb?: number;
  model?: string;
}

interface Device {
  deviceId: string;
  friendlyName?: string;
  lastSeen?: string;
  health?: DeviceHealth;
  thing?: {
    thingArn?: string;
    thingTypeName?: string;
    attributes?: Record<string, string>;
  };
  shadow?: {
    state?: {
      reported?: {
        agent_version?: string;
        os?: string;
        local_ip?: string;
        local_ips?: string[];
        uptime?: number;
        resources?: {
          cpuPercent?: number;
          memoryPercent?: number;
          diskPercent?: number;
        };
        health?: DeviceHealth;
        shell_version?: { version?: string; repo?: string; deployedAt?: string };
      };
    };
  };
  registration?: {
    sshUser: string;
    sshPassword: string;
    provisionedAt?: string;
    createdAt?: string;
  } | null;
}

interface Deploy {
  deployId: string;
  deviceId: string;
  target: string;
  version: string;
  repo: string;
  status: string;
  timestamp: string;
  error?: string;
}

export function DevicesPage() {
  const [devices, setDevices] = useState<Device[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [deploys, setDeploys] = useState<Deploy[]>([]);
  const [deploysLoading, setDeploysLoading] = useState(false);
  const [uninstalling, setUninstalling] = useState<string | null>(null);
  const [uninstallResult, setUninstallResult] = useState<{ deviceId: string; success: boolean; message: string } | null>(null);

  useEffect(() => {
    api.get("/devices").then(({ data }) => {
      setDevices(data.devices ?? []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const toggleExpand = async (deviceId: string) => {
    if (expandedId === deviceId) {
      setExpandedId(null);
      return;
    }
    setExpandedId(deviceId);
    setDeploysLoading(true);
    try {
      const { data } = await api.get(`/devices/${deviceId}/deploys`);
      setDeploys(data.deploys ?? []);
    } catch {
      setDeploys([]);
    } finally {
      setDeploysLoading(false);
    }
  };

  const isOnline = (d: Device) => {
    if (!d.lastSeen) return false;
    return Date.now() - new Date(d.lastSeen).getTime() < 120_000;
  };

  const formatUptime = (seconds: number): string => {
    const d = Math.floor(seconds / 86400);
    const h = Math.floor((seconds % 86400) / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    if (d > 0) return `${d}d ${h}h`;
    if (h > 0) return `${h}h ${m}m`;
    return `${m}m`;
  };

  const handleUninstall = async (deviceId: string) => {
    if (!confirm(`Uninstall PiStack from "${deviceId}"?\n\nThis will:\n• Send an uninstall command to the device (removes agent, certs, apps)\n• Delete the IoT thing and certificates from AWS\n• Remove the device from this dashboard\n\nThis cannot be undone.`)) {
      return;
    }
    setUninstalling(deviceId);
    setUninstallResult(null);
    try {
      await api.post(`/devices/${deviceId}/uninstall`);
      setUninstallResult({ deviceId, success: true, message: `${deviceId} uninstalled and removed from fleet.` });
      // Remove from local state
      setDevices((prev) => prev.filter((d) => d.deviceId !== deviceId));
      setExpandedId(null);
    } catch (err: any) {
      const msg = err.response?.data?.error || err.message;
      setUninstallResult({ deviceId, success: false, message: `Uninstall failed: ${msg}` });
    } finally {
      setUninstalling(null);
    }
  };

  return (
    <div className="px-8 pt-8 pb-12">
      <h1 className="text-2xl font-semibold text-surface-900">Devices</h1>
      <p className="mt-1 text-sm text-surface-500">
        {loading ? "Loading..." : `${devices.length} device${devices.length !== 1 ? "s" : ""} in fleet`}
      </p>

      {uninstallResult && (
        <div className={`mt-4 p-3 rounded-lg text-sm flex items-center gap-2 ${
          uninstallResult.success
            ? "bg-success-100 text-success-500"
            : "bg-danger-100 text-danger-500"
        }`}>
          {uninstallResult.success ? <CheckCircle2 size={16} /> : <XCircle size={16} />}
          {uninstallResult.message}
          <button
            onClick={() => setUninstallResult(null)}
            className="ml-auto text-xs opacity-60 hover:opacity-100 cursor-pointer"
          >
            Dismiss
          </button>
        </div>
      )}

      {!loading && devices.length === 0 && (
        <div className="mt-8 text-center py-16 bg-surface-0 rounded-xl border border-surface-200">
          <Cpu size={40} className="mx-auto text-surface-400 mb-4" strokeWidth={1.2} />
          <h2 className="text-lg font-medium text-surface-800">No devices yet</h2>
          <p className="mt-2 text-sm text-surface-500">Register a device in the Setup Guide to get started.</p>
        </div>
      )}

      {!loading && devices.length > 0 && (
        <div className="mt-6 bg-surface-0 rounded-xl border border-surface-200 divide-y divide-surface-200">
          {devices.map((device) => {
            const online = isOnline(device);
            const reported = device.shadow?.state?.reported;
            const health = device.health ?? reported?.health;
            const expanded = expandedId === device.deviceId;

            return (
              <div key={device.deviceId}>
                {/* Row */}
                <button
                  onClick={() => toggleExpand(device.deviceId)}
                  className="w-full px-5 py-4 flex items-center gap-4 hover:bg-surface-100/50 transition-colors cursor-pointer text-left"
                >
                  <div className="flex-shrink-0">
                    {expanded ? <ChevronDown size={16} className="text-surface-400" /> : <ChevronRight size={16} className="text-surface-400" />}
                  </div>

                  <div className="w-8 h-8 rounded-lg bg-brand-50 flex items-center justify-center flex-shrink-0">
                    <Cpu size={16} className="text-brand-500" strokeWidth={1.5} />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-surface-900 truncate">
                      {device.friendlyName || device.deviceId}
                    </div>
                    {device.friendlyName && (
                      <div className="text-[11px] text-surface-400 font-mono truncate">{device.deviceId}</div>
                    )}
                  </div>

                  <div className="flex items-center gap-6 flex-shrink-0">
                    {reported?.local_ip && reported.local_ip !== "unknown" && (
                      <span className="text-xs font-mono text-surface-500">{reported.local_ip}</span>
                    )}
                    {reported?.agent_version && (
                      <span className="text-xs text-surface-400">v{reported.agent_version}</span>
                    )}
                    <div className="flex items-center gap-1.5">
                      {online ? (
                        <>
                          <Wifi size={14} className="text-success-500" />
                          <span className="text-[11px] font-medium text-success-500">Online</span>
                        </>
                      ) : (
                        <>
                          <WifiOff size={14} className="text-surface-400" />
                          <span className="text-[11px] font-medium text-surface-400">Offline</span>
                        </>
                      )}
                    </div>
                  </div>
                </button>

                {/* Expanded detail */}
                {expanded && (
                  <div className="px-5 pb-5 pt-0 ml-[52px]">
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
                      <DetailCard label="OS" value={reported?.os || "—"} />
                      <DetailCard label="Uptime" value={reported?.uptime ? formatUptime(reported.uptime) : "—"} />
                      <DetailCard
                        label="Provisioned"
                        value={device.registration?.provisionedAt
                          ? new Date(device.registration.provisionedAt).toLocaleDateString()
                          : "—"}
                      />
                    </div>

                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
                      <DetailCard label="Local IPs" value={reported?.local_ips?.join(", ") || "—"} mono />
                      {device.registration?.sshUser && (
                        <DetailCard label="SSH User" value={device.registration.sshUser} mono />
                      )}
                      {device.registration?.sshPassword && (
                        <PasswordCard password={device.registration.sshPassword} />
                      )}
                    </div>

                    {reported?.resources && (
                      <div className="flex gap-6 mb-5">
                        <ResourceBar label="CPU" value={reported.resources.cpuPercent ?? 0} />
                        <ResourceBar label="Memory" value={reported.resources.memoryPercent ?? 0} />
                        <ResourceBar label="Disk" value={reported.resources.diskPercent ?? 0} />
                      </div>
                    )}

                    {health && (
                      <div className="mb-5">
                        <div className="text-[11px] text-surface-400 uppercase tracking-wider font-semibold mb-2">Health</div>
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                          {health.tempC !== undefined && (
                            <HealthStat label="Temp" value={`${health.tempC.toFixed(1)}°C`} tone={tempTone(health.tempC)} />
                          )}
                          {health.powerWatts !== undefined && (
                            <HealthStat label="Power" value={`${health.powerWatts.toFixed(2)} W`} />
                          )}
                          {health.cpuFreqMhz !== undefined && (
                            <HealthStat
                              label="CPU Clock"
                              value={`${(health.cpuFreqMhz / 1000).toFixed(2)} GHz`}
                              dim={health.cpuFreqMaxMhz !== undefined && health.cpuFreqMhz < health.cpuFreqMaxMhz}
                            />
                          )}
                          {health.fanRpm !== undefined && (
                            <HealthStat label="Fan" value={`${health.fanRpm} RPM`} />
                          )}
                          {health.coolingState !== undefined && (
                            <HealthStat label="Cooling" value={`Level ${health.coolingState}`} />
                          )}
                          {health.coreVolts !== undefined && (
                            <HealthStat label="Core V" value={`${health.coreVolts.toFixed(3)} V`} />
                          )}
                          {health.load1 !== undefined && (
                            <HealthStat
                              label="Load 1/5/15m"
                              value={`${health.load1.toFixed(2)} / ${health.load5?.toFixed(2) ?? "-"} / ${health.load15?.toFixed(2) ?? "-"}`}
                            />
                          )}
                          {health.memUsedMb !== undefined && health.memTotalMb !== undefined && (
                            <HealthStat
                              label="Memory"
                              value={`${(health.memUsedMb / 1024).toFixed(1)} / ${(health.memTotalMb / 1024).toFixed(1)} GB`}
                            />
                          )}
                        </div>
                        {health.throttle && <ThrottleBadges t={health.throttle} />}
                        {health.model && (
                          <div className="mt-2 text-[11px] text-surface-400">{health.model}</div>
                        )}
                      </div>
                    )}

                    {reported?.shell_version && (
                      <div className="mb-5 p-3 rounded-lg bg-surface-50 border border-surface-200">
                        <div className="text-[11px] text-surface-400 uppercase tracking-wider font-semibold mb-1">Deployed App</div>
                        <div className="text-sm text-surface-700 font-mono">
                          {reported.shell_version.repo} @ {reported.shell_version.version?.slice(0, 8)}
                        </div>
                        {reported.shell_version.deployedAt && (
                          <div className="text-xs text-surface-400 mt-1">
                            {new Date(reported.shell_version.deployedAt).toLocaleString()}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Deploy history */}
                    <div>
                      <h4 className="text-[11px] text-surface-400 uppercase tracking-wider font-semibold mb-2">
                        Recent Deploys
                      </h4>
                      {deploysLoading ? (
                        <p className="text-sm text-surface-400">Loading...</p>
                      ) : deploys.length === 0 ? (
                        <p className="text-sm text-surface-400">No deploys yet.</p>
                      ) : (
                        <div className="space-y-2">
                          {deploys.slice(0, 5).map((d) => (
                            <div key={`${d.deployId}-${d.deviceId}`} className="flex items-center gap-3 text-sm">
                              <DeployStatusIcon status={d.status} />
                              <span className="text-surface-700 font-mono text-xs">{d.version.slice(0, 8)}</span>
                              <span className="text-surface-500">{d.target}</span>
                              <span className="text-surface-400 text-xs">{d.repo}</span>
                              <span className="text-surface-400 text-xs ml-auto">
                                {new Date(d.timestamp).toLocaleString()}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Agent Logs */}
                    <AgentLogsCard deviceId={device.deviceId} sshUser={device.registration?.sshUser} ip={reported?.local_ip} />

                    {/* Uninstall */}
                    <div className="mt-5 pt-5 border-t border-surface-200">
                      <button
                        onClick={() => handleUninstall(device.deviceId)}
                        disabled={uninstalling === device.deviceId}
                        className="flex items-center gap-2 px-3 py-2 text-xs font-medium text-danger-500 hover:bg-danger-100 rounded-lg transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {uninstalling === device.deviceId ? (
                          <Loader size={14} className="animate-spin" />
                        ) : (
                          <Trash2 size={14} />
                        )}
                        {uninstalling === device.deviceId ? "Uninstalling..." : "Uninstall & Remove Device"}
                      </button>
                      <p className="mt-1.5 text-[11px] text-surface-400 ml-[30px]">
                        Sends an uninstall command to the device, then removes it from AWS IoT and this dashboard.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function AgentLogsCard({ deviceId, sshUser, ip }: { deviceId: string; sshUser?: string; ip?: string }) {
  const [expanded, setExpanded] = useState(false);
  const [copiedCmd, setCopiedCmd] = useState<string | null>(null);

  const user = sshUser || "pi";
  const host = ip && ip !== "unknown" ? ip : "<device-ip>";

  const commands = [
    { label: "Agent logs (live)", cmd: `ssh ${user}@${host} 'journalctl -u pistack-agent -f'` },
    { label: "Agent logs (last 50 lines)", cmd: `ssh ${user}@${host} 'journalctl -u pistack-agent -n 50 --no-pager'` },
    { label: "Shell app logs", cmd: `ssh ${user}@${host} 'journalctl -u pistack-shell -f'` },
    { label: "All PiStack logs", cmd: `ssh ${user}@${host} 'journalctl -u pistack-agent -u pistack-shell -f --since "10 minutes ago"'` },
  ];

  const handleCopy = (cmd: string) => {
    navigator.clipboard.writeText(cmd);
    setCopiedCmd(cmd);
    setTimeout(() => setCopiedCmd(null), 2000);
  };

  return (
    <div className="mt-5 pt-5 border-t border-surface-200">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 text-[11px] text-surface-400 uppercase tracking-wider font-semibold hover:text-surface-600 transition-colors cursor-pointer"
      >
        {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        <Terminal size={12} />
        Agent Logs
      </button>

      {expanded && (
        <div className="mt-3 space-y-2">
          <p className="text-xs text-surface-400 mb-3">
            SSH into the device to view logs. Click a command to copy it.
          </p>
          {commands.map((c) => (
            <button
              key={c.cmd}
              onClick={() => handleCopy(c.cmd)}
              className="w-full flex items-center gap-3 p-2.5 rounded-lg bg-surface-900 hover:bg-surface-800 transition-colors cursor-pointer text-left group"
            >
              <div className="flex-1 min-w-0">
                <div className="text-[10px] text-surface-500 mb-0.5">{c.label}</div>
                <div className="text-xs font-mono text-green-400 truncate">{c.cmd}</div>
              </div>
              <div className="flex-shrink-0 text-surface-500 group-hover:text-surface-300">
                {copiedCmd === c.cmd ? (
                  <Check size={14} className="text-green-400" />
                ) : (
                  <Copy size={14} />
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function tempTone(t: number): "ok" | "warn" | "bad" {
  if (t >= 80) return "bad";
  if (t >= 65) return "warn";
  return "ok";
}

function HealthStat({
  label,
  value,
  tone,
  dim,
}: {
  label: string;
  value: string;
  tone?: "ok" | "warn" | "bad";
  dim?: boolean;
}) {
  const color =
    tone === "bad"
      ? "text-danger-500"
      : tone === "warn"
      ? "text-warning-500"
      : dim
      ? "text-surface-400"
      : "text-surface-700";
  return (
    <div className="p-3 rounded-lg bg-surface-50 border border-surface-200">
      <div className="text-[11px] text-surface-400 uppercase tracking-wider font-semibold mb-1">{label}</div>
      <div className={`text-sm font-medium ${color}`}>{value}</div>
    </div>
  );
}

function ThrottleBadges({ t }: { t: ThrottleFlags }) {
  const badges = [
    { label: "Under-voltage", now: !!t.underVoltageNow, ever: !!t.underVoltageEver },
    { label: "Throttled", now: !!t.throttledNow, ever: !!t.throttledEver },
    { label: "Soft temp limit", now: !!t.softTempNow, ever: !!t.softTempEver },
    { label: "Freq capped", now: !!t.armCappedNow, ever: !!t.armCappedEver },
  ];
  const shown = badges.filter((b) => b.now || b.ever);

  if (shown.length === 0) {
    return (
      <div className="mt-2 flex items-center gap-1.5 text-xs text-success-500">
        <CheckCircle2 size={13} /> No throttling
      </div>
    );
  }

  return (
    <div className="mt-2 flex flex-wrap gap-2">
      {shown.map((b) => (
        <span
          key={b.label}
          className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${
            b.now ? "bg-danger-100 text-danger-500" : "bg-warning-100 text-warning-500"
          }`}
        >
          {b.label}
          {b.now ? "" : " (since boot)"}
        </span>
      ))}
    </div>
  );
}

function DetailCard({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="p-3 rounded-lg bg-surface-50 border border-surface-200">
      <div className="text-[11px] text-surface-400 uppercase tracking-wider font-semibold mb-1">{label}</div>
      <div className={`text-sm text-surface-700 truncate ${mono ? "font-mono" : ""}`}>{value}</div>
    </div>
  );
}

function PasswordCard({ password }: { password: string }) {
  const [visible, setVisible] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(password);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="p-3 rounded-lg bg-surface-50 border border-surface-200">
      <div className="text-[11px] text-surface-400 uppercase tracking-wider font-semibold mb-1">SSH Password</div>
      <div className="flex items-center gap-1.5">
        <span className="text-sm font-mono text-surface-700 select-all truncate">
          {visible ? password : "••••••••••••"}
        </span>
        <button
          onClick={() => setVisible(!visible)}
          className="p-0.5 text-surface-400 hover:text-surface-600 rounded transition-colors cursor-pointer flex-shrink-0"
          title={visible ? "Hide" : "Show"}
        >
          {visible ? <EyeOff size={12} /> : <Eye size={12} />}
        </button>
        <button
          onClick={handleCopy}
          className="p-0.5 text-surface-400 hover:text-surface-600 rounded transition-colors cursor-pointer flex-shrink-0"
          title="Copy"
        >
          {copied ? <Check size={12} className="text-success-500" /> : <Copy size={12} />}
        </button>
      </div>
    </div>
  );
}

function ResourceBar({ label, value }: { label: string; value: number }) {
  const color = value > 90 ? "bg-danger-500" : value > 70 ? "bg-warning-500" : "bg-success-500";
  return (
    <div className="flex-1">
      <div className="flex justify-between text-[10px] text-surface-400 font-mono mb-1">
        <span>{label}</span>
        <span>{value}%</span>
      </div>
      <div className="h-1.5 bg-surface-200 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${value}%` }} />
      </div>
    </div>
  );
}

function DeployStatusIcon({ status }: { status: string }) {
  switch (status) {
    case "success":
      return <CheckCircle2 size={14} className="text-success-500 flex-shrink-0" />;
    case "failed":
      return <XCircle size={14} className="text-danger-500 flex-shrink-0" />;
    case "rolled-back":
      return <RotateCcw size={14} className="text-warning-500 flex-shrink-0" />;
    case "in-progress":
      return <Rocket size={14} className="text-brand-400 flex-shrink-0" />;
    default:
      return <Clock size={14} className="text-surface-400 flex-shrink-0" />;
  }
}

